// Derive filter tags for a product from its name / material / dimensions / features / story.
// Used both by the Collections filter (Shop by Size / Style / Room) and by the Admin "Filter Mapping" view.

const STYLE_KEYWORDS = ["modern", "abstract", "geometric", "traditional", "minimal", "bohemian"];

const ROOM_KEYWORDS = {
  "living room": ["living room", "living-room", "living"],
  "bedroom": ["bedroom", "bed room", "bed-room"],
  "dining area": ["dining", "kitchen"],
  "entryway": ["entryway", "entrance", "hallway", "door", "doormat", "corridor"],
  "kids' room": ["kids", "kid's", "child", "baby", "nursery", "toy"],
};

/**
 * Parse an "L × W" or "L * W" or "L x W" or "L Feet x W Feet" style dimensions string.
 * Returns { l, w, unit } if possible, else null.
 */
function parseDimensions(text) {
  if (!text) return null;
  let s = String(text).toLowerCase().replace(/[×✕]/g, "x").replace(/\*/g, "x");
  // Match: "4 feet x 6 feet", "4 ft x 6 ft", "4 x 6 feet", "4x6", "2.6 x 8 feet"
  const m = s.match(/(\d+(?:\.\d+)?)\s*(?:feet|ft|inch|inches|in|cm|m)?\s*x\s*(\d+(?:\.\d+)?)\s*(feet|ft|inch|inches|in|cm|m)?/);
  if (m) {
    const l = parseFloat(m[1]);
    const w = parseFloat(m[2]);
    const trailingUnit = (m[3] || "").toLowerCase();
    const leadingUnitMatch = s.slice(0, m.index + m[0].length).match(/(feet|ft|inch|inches|in|cm|m)/);
    const unitStr = trailingUnit || (leadingUnitMatch ? leadingUnitMatch[1] : "ft");
    const unit = unitStr.startsWith("f") ? "ft" : unitStr.startsWith("in") ? "in" : unitStr;
    return { l, w, unit };
  }
  // Single-value like "4 feet (round)"
  const single = s.match(/(\d+(?:\.\d+)?)\s*(feet|ft|inch|inches|in)\s*\(?\s*(round|circle)?/);
  if (single) {
    const l = parseFloat(single[1]);
    const unit = single[2].startsWith("f") ? "ft" : "in";
    return { l, w: l, unit, round: !!single[3] };
  }
  return null;
}

function areaSqFt(dim) {
  if (!dim) return null;
  if (dim.unit === "in") return (dim.l * dim.w) / 144;
  return dim.l * dim.w;
}

function aspectRatio(dim) {
  if (!dim) return null;
  const [a, b] = [dim.l, dim.w].sort((x, y) => y - x);
  return a / b;
}

/**
 * Compute a set of tags for a product.
 * If the product has manual overrides (tags_sizes / tags_styles / tags_rooms),
 * those replace the auto-derived tags. Empty arrays fall back to auto tags.
 * @returns {{ sizes: string[], styles: string[], rooms: string[], material: string, filters: string[], manual: { sizes: boolean, styles: boolean, rooms: boolean } }}
 */
export function deriveTags(product) {
  const haystack = [
    product.name || "",
    product.story || "",
    (product.features || []).join(" "),
    product.material || "",
    product.dimensions || "",
  ]
    .join(" ")
    .toLowerCase();

  // -------- Size --------
  const autoSizes = [];
  const dim =
    parseDimensions(product.dimensions) ||
    parseDimensions(product.name) ||
    null;
  const area = areaSqFt(dim);
  const ratio = aspectRatio(dim);
  const isRug = product.category === "carpets" || product.category === "rugs";

  // Runner: aspect ratio >= 2 OR explicit "runner" mention — rugs/carpets only
  if (isRug && (haystack.includes("runner") || (ratio && ratio >= 2))) {
    autoSizes.push("runner rugs");
  }

  // Exact common sizes (feet only, and only for carpets/rugs)
  if (dim && dim.unit === "ft" && isRug) {
    const pair = [dim.l, dim.w].sort((a, b) => a - b).join("×");
    if (pair === "4×6") autoSizes.push("4 × 6 ft");
    if (pair === "5×7") autoSizes.push("5 × 7 ft");
    if (pair === "5×8") autoSizes.push("5 × 8 ft");
  }

  // Small / Large by area (only for rugs)
  if (area && isRug) {
    if (area <= 20) autoSizes.push("small rugs");
    else if (area >= 35) autoSizes.push("large rugs");
  }

  // -------- Style --------
  const autoStyles = STYLE_KEYWORDS.filter((k) => haystack.includes(k));

  // -------- Room --------
  const autoRooms = [];
  for (const [label, keywords] of Object.entries(ROOM_KEYWORDS)) {
    if (keywords.some((k) => haystack.includes(k))) autoRooms.push(label);
  }

  // -------- Manual overrides --------
  const hasManualSizes = Array.isArray(product.tags_sizes) && product.tags_sizes.length > 0;
  const hasManualStyles = Array.isArray(product.tags_styles) && product.tags_styles.length > 0;
  const hasManualRooms = Array.isArray(product.tags_rooms) && product.tags_rooms.length > 0;

  const sizes = hasManualSizes ? product.tags_sizes.map((s) => s.toLowerCase()) : autoSizes;
  const styles = hasManualStyles ? product.tags_styles.map((s) => s.toLowerCase()) : autoStyles;
  const rooms = hasManualRooms ? product.tags_rooms.map((s) => s.toLowerCase()) : autoRooms;

  const material = (product.material || "").toLowerCase();

  return {
    sizes,
    styles,
    rooms,
    material,
    filters: [],
    manual: { sizes: hasManualSizes, styles: hasManualStyles, rooms: hasManualRooms },
    _area: area,
    _dim: dim,
    _auto: { sizes: autoSizes, styles: autoStyles, rooms: autoRooms },
  };
}

/**
 * Check whether a product matches the given search-param filters.
 * All non-null params must match (AND). Values are lowercased strings.
 */
export function matchesFilters(product, params) {
  const tags = deriveTags(product);

  if (params.cat && params.cat !== "all" && product.category !== params.cat) return false;
  if (params.material && !tags.material.includes(params.material.toLowerCase())) return false;
  if (params.style && !tags.styles.includes(params.style.toLowerCase())) return false;
  if (params.size && !tags.sizes.includes(params.size.toLowerCase())) return false;
  if (params.room && !tags.rooms.includes(params.room.toLowerCase())) return false;

  return true;
}

export const SIZE_OPTIONS = [
  "small rugs",
  "runner rugs",
  "4 × 6 ft",
  "5 × 7 ft",
  "5 × 8 ft",
  "large rugs",
];
export const STYLE_OPTIONS = STYLE_KEYWORDS;
export const ROOM_OPTIONS = Object.keys(ROOM_KEYWORDS);
